import { createCrudEndpoints } from '@/api';

import { completeJob, getApprovals, getFleetJobInstances, setupCalendar, setupRunningHours } from './customApi';
import {
  vesselJobApiResponseSchema,
  vesselJobArrayApiResponseSchema,
  vesselJobArraySchema,
  vesselJobCoreApiResponseSchema,
  vesselJobCoreArrayApiResponseSchema,
  vesselJobCoreArraySchema,
  vesselJobCoreSchema,
  vesselJobExApiResponseSchema,
  vesselJobExArrayApiResponseSchema,
  vesselJobExArraySchema,
  vesselJobExSchema,
  vesselJobInfoArrayApiResponseSchema,
  vesselJobInfoArraySchema,
  vesselJobInfoSchema,
  vesselJobSchema,
  type VesselJob,
  type VesselJobCore,
  type VesselJobEx,
  type VesselJobInfo
} from './schema';

// Re-export ResponseTypeMap for backward compatibility
export interface ResponseTypeMap {
  flat: VesselJob[];
  hierarchy: VesselJobEx[];
  info: VesselJobInfo[];
  core: VesselJobCore[];
}

// Fleet Jobs Dashboard criteria
export interface FleetJobsCriteria {
  [key: string]: string | number | boolean | undefined;
  VesselComponentId?: string;
  IsCriticalComponent?: boolean;
  IsCriticalJob?: boolean;
  VesselId?: string;
  VesselIdsText?: string;
  AbstractComponentCategoryIdsText?: string;
  IntervalType?: string;
  IntervalTypesText?: string;
  IncludeNullIntervalType?: boolean;
  ResponsibleOfficerIdsText?: string;
  IsDryDock?: boolean;
  AttributeFlagsText?: string;
  DueDaysFrom?: number;
  DueDaysTo?: number;
  ExcludeStatus?: string;
  Status?: string;
}

export interface ApprovalsCriteria {
  [key: string]: string | undefined;
  Types?: string;
  VesselNames?: string;
  Statuses?: string;
}

// Setup Running Hours types
export interface SetupRunningHoursRequest {
  Date: string;
  CurrentHours: number;
  RunningHoursType: 'LastDone' | 'NextDue';
  RunningHours: number;
}

// Setup Calendar types
export interface SetupCalendarRequest {
  Date: string;
  CalendarType: 'LastDone' | 'NextDue';
}

// ============ BASE CRUD ENDPOINTS ============

const baseEndpoints = createCrudEndpoints<VesselJobCore, VesselJob, VesselJobEx, VesselJobInfo>({
  baseUrl: '/pms/api/vesselJobs',
  entityName: 'VesselJob',
  schemas: {
    core: vesselJobCoreSchema,
    flat: vesselJobSchema,
    ex: vesselJobExSchema,
    info: vesselJobInfoSchema,
    coreArray: vesselJobCoreArraySchema,
    flatArray: vesselJobArraySchema,
    exArray: vesselJobExArraySchema,
    infoArray: vesselJobInfoArraySchema,
    coreApiResponse: vesselJobCoreApiResponseSchema,
    flatApiResponse: vesselJobApiResponseSchema,
    exApiResponse: vesselJobExApiResponseSchema,
    coreArrayApiResponse: vesselJobCoreArrayApiResponseSchema,
    flatArrayApiResponse: vesselJobArrayApiResponseSchema,
    exArrayApiResponse: vesselJobExArrayApiResponseSchema,
    infoArrayApiResponse: vesselJobInfoArrayApiResponseSchema
  }
});

// ============ COMPOSED EXPORT ============

export const vesselJobsEndpoints = {
  ...baseEndpoints,
  getVesselJobsCore: baseEndpoints.getAllCore,
  getVesselJobsFlat: baseEndpoints.getAllFlat,
  getVesselJobs: baseEndpoints.getAllHierarchy,
  getVesselJobsInfo: baseEndpoints.getAllInfo,
  getVesselJobById: baseEndpoints.getById,
  getVesselJobCoreById: baseEndpoints.getCoreById,
  createVesselJobCore: baseEndpoints.createCore,
  createVesselJob: baseEndpoints.create,
  createVesselJobEx: baseEndpoints.createEx,
  updateVesselJobCore: baseEndpoints.updateCore,
  updateVesselJob: baseEndpoints.update,
  updateVesselJobEx: baseEndpoints.updateEx,
  deleteVesselJob: baseEndpoints.delete,
  getFleetJobInstances,
  getApprovals,
  setupRunningHours,
  setupCalendar,
  completeJob
};
